import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { format, parseISO, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FaArrowLeft, FaCalendarAlt, FaCheck, FaHeadset, FaPaperclip, FaTicketAlt, FaTimes, FaUser } from 'react-icons/fa';
import { getStatusClass, getStatusLabel } from '../../services/statusService';
import { getSub } from '../../services/api';
import {
  commentOnMyWidgetTicket,
  getMyWidgetTicket,
  listMyWidgetTicketAttachments,
  listMyWidgetTicketMessages,
  listMyWidgetTickets,
  resolveMyWidgetTicket,
} from '../../services/widgetMyTicketsService';
import DescriptionCompareModal from '../../components/tickets/DescriptionCompareModal';
import {
  parseDescriptionChangeMessage,
  splitTextAtDescriptionWord,
} from '../../utils/descriptionChangeMessage';
import TicketProtocolLabel from '../../components/shared/TicketProtocolLabel';

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #f0f0f0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  flex: 1;
  font-size: 0.9rem;
  font-weight: 700;
  color: #2d3436;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const IconButton = styled.button`
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #2d3436;
`;

const Body = styled.div`
  padding: 0.75rem;
`;

const TicketRow = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 10px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #b2bec3;
  }
`;

const TicketTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #2d3436;
  line-height: 1.35;
`;

const TicketMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
`;

const TicketDate = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  color: #64748b;
`;

const STATUS_PILL_STYLES = {
  open: { background: '#e6f4ff', color: '#1890ff' },
  in_progress: { background: '#fff7e6', color: '#d48806' },
  pending: { background: '#fff0f6', color: '#eb2f96' },
  resolved: { background: '#e6ffe6', color: '#52c41a' },
  closed: { background: '#f5f5f5', color: '#8c8c8c' },
};

const StatusPill = styled.span`
  align-self: flex-start;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: ${({ $status }) =>
    (STATUS_PILL_STYLES[$status] || STATUS_PILL_STYLES.open).background};
  color: ${({ $status }) =>
    (STATUS_PILL_STYLES[$status] || STATUS_PILL_STYLES.open).color};
`;

const Empty = styled.div`
  text-align: center;
  color: #636e72;
  font-size: 0.9rem;
  padding: 2rem 1rem;
`;

const DetailPanel = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 0.9rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DetailHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const DetailTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #2d3436;
  line-height: 1.35;
`;

const DetailText = styled.div`
  font-size: 0.9rem;
  color: #636e72;
  line-height: 1.45;
  white-space: pre-wrap;
`;

const ResolveButton = styled.button`
  align-self: flex-start;
  border: none;
  border-radius: 10px;
  padding: 0.55rem 0.9rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: #059669;
  color: #fff;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  border-top: 1px solid #eef1f4;
  padding-top: 0.75rem;
`;

const MessageCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  max-width: 92%;
  align-self: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
  background: ${({ $mine, $support }) =>
    $mine ? '#eff6ff' : $support ? '#f0f9ff' : '#f8fafc'};
  border-left: ${({ $mine, $support }) =>
    $mine ? 'none' : `3px solid ${$support ? '#0ea5e9' : '#3b82f6'}`};
  border-right: ${({ $mine }) => ($mine ? '3px solid #3b82f6' : 'none')};
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  flex-direction: ${({ $mine }) => ($mine ? 'row-reverse' : 'row')};
`;

const MessageAuthorInfo = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  min-width: 0;
  font-size: 0.78rem;
  color: #1e293b;
  justify-content: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
  text-align: ${({ $mine }) => ($mine ? 'right' : 'left')};
`;

const MessageAuthor = styled.span`
  font-weight: 600;
`;

const SupportBadge = styled.span`
  background: #0ea5e9;
  color: #fff;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.65rem;
  font-weight: 600;
`;

const MessageTime = styled.span`
  flex-shrink: 0;
  font-size: 0.68rem;
  color: #64748b;
  line-height: 1.3;
  text-align: right;
`;

const MessageBody = styled.div`
  font-size: 0.85rem;
  color: #334155;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: ${({ $mine }) => ($mine ? 'right' : 'left')};
`;

const DescriptionChangeLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
  line-height: inherit;

  &:hover {
    color: #1d4ed8;
  }

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const Composer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-top: 1px solid #eef1f4;
  padding-top: 0.75rem;
`;

const ComposerRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const TextArea = styled.textarea`
  flex: 1;
  min-height: 56px;
  resize: vertical;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
  font: inherit;
  color: #2d3436;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #b2bec3;
  }
`;

const AttachButton = styled.label`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #2d3436;
  background: #fff;
  flex-shrink: 0;

  input {
    display: none;
  }
`;

const PendingFiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const PendingChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  color: #636e72;
  background: #f1f3f5;
  border-radius: 999px;
  padding: 0.2rem 0.45rem 0.2rem 0.55rem;
  max-width: 100%;
`;

const PendingName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
`;

const RemoveFileButton = styled.button`
  border: none;
  background: transparent;
  color: #636e72;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
`;

const SendButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0.55rem 0.9rem;
  font-weight: 700;
  cursor: pointer;
  background: #2d3436;
  color: #fff;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const AttachmentsGrid = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const AttachmentThumb = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  background: #f8f9fa;
  cursor: pointer;
  width: 56px;
  height: 56px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1rem;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  border: none;
  border-radius: 999px;
  width: 2.25rem;
  height: 2.25rem;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const LightboxImage = styled.img`
  max-width: min(920px, 100%);
  max-height: 85vh;
  border-radius: 8px;
  object-fit: contain;
`;

const isResolvedStatus = (status) => {
  const key = String(status || '').toUpperCase();
  return key === 'RESOLVED' || key.includes('RESOLV') || key === 'CLOSED';
};

const formatMessageDate = (date) => {
  try {
    if (!date || date === 'null' || date === 'undefined') {
      return 'Data inválida';
    }
    const messageDate = parseISO(date);
    if (isToday(messageDate)) {
      return `Hoje às ${format(messageDate, 'HH:mm')}`;
    }
    if (isYesterday(messageDate)) {
      return `Ontem às ${format(messageDate, 'HH:mm')}`;
    }
    return format(messageDate, "d 'de' MMMM 'às' HH:mm", { locale: ptBR });
  } catch (error) {
    console.error('Erro ao formatar data da mensagem:', error);
    return 'Data inválida';
  }
};

const formatTicketDate = (dateString) => {
  if (!dateString) return null;
  try {
    return format(parseISO(dateString), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch (error) {
    console.error('Erro ao formatar data do ticket:', error);
    return null;
  }
};

const isSupportMessage = (msg) =>
  Boolean(msg?.supportResponse ?? msg?.isSupportResponse);

const isOwnMessage = (msg, currentEmail) => {
  if (!currentEmail) return false;
  const authorEmail = msg?.createdBy?.email;
  if (!authorEmail) return false;
  return String(authorEmail).trim().toLowerCase() === String(currentEmail).trim().toLowerCase();
};

const EmbedMessageContent = ({ content }) => {
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const { displayText, meta } = parseDescriptionChangeMessage(content);

  if (!meta) {
    return <>{displayText}</>;
  }

  const { before, link, after } = splitTextAtDescriptionWord(displayText);

  return (
    <>
      {before}
      {link ? (
        <DescriptionChangeLink type="button" onClick={() => setCompareModalOpen(true)}>
          {link}
        </DescriptionChangeLink>
      ) : null}
      {after}
      <DescriptionCompareModal
        open={compareModalOpen}
        oldDescription={meta.oldDescription}
        newDescription={meta.newDescription}
        onClose={() => setCompareModalOpen(false)}
      />
    </>
  );
};

const EmbedMyTicketsPanel = ({ onClose }) => {
  const currentEmail = getSub();
  const [view, setView] = useState('list');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listMyWidgetTickets({ page: 0, size: 50 });
      setTickets(response.content || []);
    } catch (err) {
      console.error(err);
      setError('Não foi possível carregar seus tickets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openDetail = async (ticketId) => {
    setSelectedId(ticketId);
    setView('detail');
    setDetailLoading(true);
    setError(null);
    setComment('');
    setPendingFiles([]);
    try {
      const [ticket, msgs, files] = await Promise.all([
        getMyWidgetTicket(ticketId),
        listMyWidgetTicketMessages(ticketId),
        listMyWidgetTicketAttachments(ticketId),
      ]);
      setDetail(ticket);
      setMessages(Array.isArray(msgs) ? msgs : []);
      setAttachments(Array.isArray(files) ? files : []);
    } catch (err) {
      console.error(err);
      setError('Não foi possível abrir o ticket.');
    } finally {
      setDetailLoading(false);
    }
  };

  const backToList = () => {
    setView('list');
    setSelectedId(null);
    setDetail(null);
    setMessages([]);
    setAttachments([]);
    setSelectedImage(null);
    setComment('');
    setPendingFiles([]);
    loadList();
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });

  const onSelectFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onResolve = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await resolveMyWidgetTicket(selectedId);
      const ticket = await getMyWidgetTicket(selectedId);
      setDetail(ticket);
    } catch (err) {
      console.error(err);
      setError('Não foi possível marcar como resolvido.');
    } finally {
      setSaving(false);
    }
  };

  const onComment = async (e) => {
    e.preventDefault();
    if (!selectedId || (!comment.trim() && pendingFiles.length === 0)) return;
    setSaving(true);
    try {
      const attachmentsBase64 = await Promise.all(pendingFiles.map((file) => convertToBase64(file)));
      const created = await commentOnMyWidgetTicket(
        selectedId,
        comment.trim(),
        attachmentsBase64
      );
      setMessages((prev) => [
        {
          ...created,
          createdDate: created.createdDate || new Date().toISOString(),
        },
        ...prev,
      ]);
      setComment('');
      setPendingFiles([]);
      if (attachmentsBase64.length > 0) {
        const files = await listMyWidgetTicketAttachments(selectedId);
        setAttachments(Array.isArray(files) ? files : []);
      }
    } catch (err) {
      console.error(err);
      setError('Não foi possível enviar o comentário.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell>
      <Header>
        {view === 'detail' ? (
          <IconButton type="button" onClick={backToList} aria-label="Voltar">
            <FaArrowLeft />
          </IconButton>
        ) : null}
        <HeaderTitle>
          <FaTicketAlt aria-hidden />
          Tickets
        </HeaderTitle>
        {onClose ? (
          <IconButton type="button" onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </IconButton>
        ) : null}
      </Header>

      <Body>
        {error ? <Empty>{error}</Empty> : null}

        {view === 'list' && loading ? <Empty>Carregando...</Empty> : null}

        {view === 'list' && !loading && !error && tickets.length === 0 ? (
          <Empty>Nenhum ticket encontrado.</Empty>
        ) : null}

        {view === 'list' &&
          !loading &&
          tickets.map((ticket) => {
            const ticketDate = formatTicketDate(ticket.creationDate);
            return (
              <TicketRow key={ticket.id} type="button" onClick={() => openDetail(ticket.id)}>
                <TicketProtocolLabel ticket={ticket} compact />
                <TicketTitle>{ticket.subjectDescription || 'Sem título'}</TicketTitle>
                <TicketMeta>
                  <StatusPill $status={getStatusClass(ticket.status)}>
                    {getStatusLabel(ticket.status) || ticket.status}
                  </StatusPill>
                  {ticketDate ? (
                    <TicketDate>
                      <FaCalendarAlt aria-hidden />
                      {ticketDate}
                    </TicketDate>
                  ) : null}
                </TicketMeta>
              </TicketRow>
            );
          })}

        {view === 'detail' && detailLoading ? <Empty>Carregando...</Empty> : null}

        {view === 'detail' && !detailLoading && detail ? (
          <DetailPanel>
            <DetailHead>
              <TicketProtocolLabel ticket={detail} compact />
              <DetailTitle>{detail.subjectDescription || 'Ticket'}</DetailTitle>
              <TicketMeta>
                <StatusPill $status={getStatusClass(detail.status)}>
                  {getStatusLabel(detail.status) || detail.status}
                </StatusPill>
                {formatTicketDate(detail.creationDate) ? (
                  <TicketDate>
                    <FaCalendarAlt aria-hidden />
                    {formatTicketDate(detail.creationDate)}
                  </TicketDate>
                ) : null}
              </TicketMeta>
            </DetailHead>

            <DetailText>
              {detail.detailedDescription || detail.description || 'Sem descrição'}
            </DetailText>

            {attachments.length > 0 ? (
              <AttachmentsGrid>
                {attachments.map((file) => (
                  <AttachmentThumb
                    key={file.id || file.url}
                    type="button"
                    onClick={() => setSelectedImage(file.url)}
                    aria-label={file.fileName || 'Anexo'}
                  >
                    <img src={file.url} alt={file.fileName || 'Anexo'} />
                  </AttachmentThumb>
                ))}
              </AttachmentsGrid>
            ) : null}

            {!isResolvedStatus(detail.status) ? (
              <ResolveButton type="button" onClick={onResolve} disabled={saving}>
                <FaCheck />
                Marcar resolvido
              </ResolveButton>
            ) : null}

            <Composer onSubmit={onComment}>
              {pendingFiles.length > 0 ? (
                <PendingFiles>
                  {pendingFiles.map((file, index) => (
                    <PendingChip key={`${file.name}-${index}`}>
                      <PendingName title={file.name}>{file.name}</PendingName>
                      <RemoveFileButton
                        type="button"
                        onClick={() => removePendingFile(index)}
                        aria-label={`Remover ${file.name}`}
                      >
                        <FaTimes />
                      </RemoveFileButton>
                    </PendingChip>
                  ))}
                </PendingFiles>
              ) : null}
              <ComposerRow>
                <TextArea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Escreva um comentário..."
                  maxLength={4000}
                />
                <AttachButton title="Anexar arquivos">
                  <input type="file" multiple onChange={onSelectFiles} />
                  <FaPaperclip aria-hidden />
                </AttachButton>
                <SendButton
                  type="submit"
                  disabled={saving || (!comment.trim() && pendingFiles.length === 0)}
                >
                  Comentar
                </SendButton>
              </ComposerRow>
            </Composer>

            <Thread>
              {messages.length === 0 ? (
                <Empty style={{ padding: '0.25rem 0' }}>Nenhum comentário ainda.</Empty>
              ) : (
                messages.map((msg) => {
                  const support = isSupportMessage(msg);
                  const mine = isOwnMessage(msg, currentEmail);
                  return (
                    <MessageCard key={msg.id || msg.createdDate} $support={support} $mine={mine}>
                      <MessageHeader $mine={mine}>
                        <MessageAuthorInfo $mine={mine}>
                          {support ? <FaHeadset aria-hidden /> : <FaUser aria-hidden />}
                          <MessageAuthor>
                            {msg.createdBy?.name || 'Usuário Desconhecido'}
                          </MessageAuthor>
                          {support ? <SupportBadge>Equipe de Suporte</SupportBadge> : null}
                        </MessageAuthorInfo>
                        <MessageTime>{formatMessageDate(msg.createdDate)}</MessageTime>
                      </MessageHeader>
                      <MessageBody $mine={mine}>
                        <EmbedMessageContent content={msg.content} />
                      </MessageBody>
                    </MessageCard>
                  );
                })
              )}
            </Thread>
          </DetailPanel>
        ) : null}
      </Body>

      {selectedImage ? (
        <Lightbox onClick={() => setSelectedImage(null)}>
          <LightboxClose type="button" onClick={() => setSelectedImage(null)} aria-label="Fechar">
            <FaTimes />
          </LightboxClose>
          <LightboxImage
            src={selectedImage}
            alt="Anexo"
            onClick={(e) => e.stopPropagation()}
          />
        </Lightbox>
      ) : null}
    </Shell>
  );
};

export default EmbedMyTicketsPanel;
